import { AxiosError, isAxiosError } from "axios"
import { AlertCircle } from "lucide-react"

type ApiErroPageProps = {
  error: AxiosError<ApiError> | ApiError
}

// Type guards for ApiError.cause
type ErrorCauseWithErrors = {
  errors: Array<{
    error: string
    message: string | { error: string; message: string }[]
  }>
}

type ErrorCauseWithStack = {
  name: string
  message: string
  stack: string
}

function isErrorCauseWithErrors(
  cause: unknown
): cause is ErrorCauseWithErrors {
  return (
    typeof cause === "object" &&
    cause !== null &&
    "errors" in cause &&
    Array.isArray((cause as ErrorCauseWithErrors).errors)
  )
}

function isErrorCauseWithStack(
  cause: unknown
): cause is ErrorCauseWithStack {
  return (
    typeof cause === "object" &&
    cause !== null &&
    "name" in cause &&
    "message" in cause &&
    "stack" in cause
  )
}

const ApiConfigErrorPage = ({
  error,
}: ApiErroPageProps) => {
  console.log(error)

  // Extract error details based on error type
  const apiError: ApiError = isAxiosError(error)
    ? error.response?.data || {
        name: "UnknownError",
        propagable: false,
        responseCode: error.response?.status || 500,
        message: error.message,
      }
    : error

  const errorMessage = apiError.message
  const requestId = apiError.requestId

  // Extract cause details
  const causeMessages: string[] = []
  if (apiError.cause) {
    if (isErrorCauseWithErrors(apiError.cause)) {
      // Handle errors array
      apiError.cause.errors.forEach((err) => {
        if (typeof err.message === "string") {
          causeMessages.push(err.message)
        } else if (Array.isArray(err.message)) {
          err.message.forEach((nestedErr) => {
            causeMessages.push(nestedErr.message)
          })
        }
      })
    } else if (isErrorCauseWithStack(apiError.cause)) {
      // Handle stack trace error
      causeMessages.push(apiError.cause.message)
    }
  }

  return (
    <main
      className="flex min-h-screen items-center justify-center bg-background"
      style={{
        backgroundImage: `
        linear-gradient(to right, #d1d5db 1px, transparent 1px),
        linear-gradient(to bottom, #d1d5db 1px, transparent 1px)
      `,
        backgroundSize: "32px 32px",
        WebkitMaskImage:
          "radial-gradient(ellipse 60% 60% at 50% 50%, #000 30%, transparent 70%)",
        maskImage:
          "radial-gradient(ellipse 60% 60% at 50% 50%, #000 30%, transparent 70%)",
      }}
    >
      <article className="w-full max-w-md rounded-lg bg-card px-6 py-8 text-center shadow-lg">
        <AlertCircle className="mx-auto h-12 w-12 text-destructive" />
        <h1 className="mt-4 text-3xl font-bold tracking-tight text-foreground">
          Ops! Algo deu errado
        </h1>
        <p className="mt-2 text-base text-muted-foreground">
          Pedimos desculpas pelo inconveniente. Ocorreu um
          erro na requisição de configurações
        </p>

        {errorMessage && (
          <p className="mt-4 text-sm text-muted-foreground">
            {errorMessage}
          </p>
        )}

        {causeMessages.length > 0 && (
          <div className="mt-4 space-y-2">
            {causeMessages.map((msg, index) => (
              <p
                key={index}
                className="text-sm text-muted-foreground"
              >
                • {msg}
              </p>
            ))}
          </div>
        )}

        {requestId && (
          <p className="mt-4 text-sm text-muted-foreground">
            requestId: {requestId}
          </p>
        )}

        <p className="mt-4 text-sm text-muted-foreground">
          Se o problema persistir, entre em contato com
          nossa equipe de suporte.
        </p>
      </article>
    </main>
  )
}

export default ApiConfigErrorPage
